import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

const AutocompleteSelect = ({
  value,
  setValue,
  options = [],
  placeholder = "Buscar...",
  onSelectOption,
  minChars = 0,
  maxHeight = "max-h-56",
  className = "",
  width,
}) => {
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState({})
  const inputRef = useRef(null)

  useEffect(() => {
    const selected = options.find(o => o.value === value)
    if (selected) {
      setQuery(selected.label)
    } else if (!value) {
      setQuery("")
    }
  }, [value, options])

  const filtered = useMemo(() => {
    const lower = (query || "").toLowerCase()
    return options.filter(o => o.label?.toLowerCase().includes(lower))
  }, [options, query])

  const updatePosition = useCallback(() => {
    if (!inputRef.current) return
    const rect = inputRef.current.getBoundingClientRect()
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePosition()
    window.addEventListener("scroll", updatePosition, true)
    window.addEventListener("resize", updatePosition)
    return () => {
      window.removeEventListener("scroll", updatePosition, true)
      window.removeEventListener("resize", updatePosition)
    }
  }, [open, updatePosition])

  const dropdown = open && filtered.length > 0 && createPortal(
    <ul
      className={`bg-white shadow-lg border border-gray-300 rounded-lg ${maxHeight} overflow-auto`}
      style={{ ...dropdownStyle, zIndex: 99999 }}
    >
      {filtered.map((opt) => (
        <li
          key={opt.value}
          className="px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 cursor-pointer"
          onMouseDown={() => {
            setValue(opt.value)
            setQuery(opt.label)
            if (onSelectOption) onSelectOption(opt)
            setOpen(false)
          }}
        >
          {opt.label}
        </li>
      ))}
    </ul>,
    document.body
  )

  return (
    <div className={`relative ${className}`} style={width ? { width } : undefined}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(e.target.value.length >= minChars) }}
        onFocus={() => { setOpen((query || "").length >= minChars); updatePosition() }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="text-gray-700 text-xs bg-white border hover:cursor-pointer border-gray-300 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent py-[6px] pl-3 pr-8 w-full"
      />
      {(query || value) && (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
          onMouseDown={(e) => {
            e.preventDefault()
            setQuery("")
            setValue("")
            if (onSelectOption) onSelectOption(null)
            setOpen(false)
          }}
        >
          ×
        </button>
      )}
      {dropdown}
    </div>
  )
}

export default AutocompleteSelect;
